import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, RefreshCw, Search, Pencil,
  CheckCircle, XCircle, AlertTriangle, Loader, ChevronLeft, ChevronRight,
  Building2, Server, Zap, Package, CreditCard, Palette, User, ClipboardList
} from 'lucide-react';
import { THEMES } from '../../utils/brandTheme';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  listTenants, activateTenant, deactivateTenant,
  onboardTenant, updateTenant, listChannels, listPlans,
  testSapConnection,
} from '../../utils/adminApi';

// ── Constants ─────────────────────────────────────────────────────────────────

const MODULES = ['MM', 'SD', 'FI', 'PP', 'EWM', 'ITSM', 'QM', 'PM'];
const INDUSTRIES = ['Manufacturing', 'Retail', 'Logistics', 'Healthcare', 'IT Services', 'Finance', 'Energy', 'Other'];
const SAP_VERSIONS = ['S/4HANA', 'ECC'];
const PLAN_LIMITS = { starter: 25, business: 100, enterprise: 999 };

const WIZARD_STEPS = [
  { label: 'Company',   icon: Building2 },
  { label: 'SAP',       icon: Server },
  { label: 'Test SAP',  icon: Zap },
  { label: 'Modules',   icon: Package },
  { label: 'Plan',      icon: CreditCard },
  { label: 'Branding',  icon: Palette },
  { label: 'Admin',     icon: User },
  { label: 'Summary',   icon: ClipboardList },
];

const EMPTY_WIZARD = {
  // Step 1 — Company
  tenant_id: '', name: '', industry: 'Manufacturing',
  contact_name: '', contact_email: '',
  channel_id: 'direct',
  // Step 2 — SAP
  sap_base_url: '', sap_username: '', sap_password: '',
  sap_client: '100', sap_version: 'S/4HANA',
  // Step 3 — Test (result stored here)
  sap_test_result: null, // { success, message, response_time_ms }
  // Step 4 — Modules
  modules: ['MM', 'SD', 'FI'],
  // Step 5 — Plan
  plan: 'starter', max_users: 25, licence_expiry: '',
  // Step 6 — Branding
  brand_name: '', brand_color: '#1a73e8', brand_theme: 'default',
  // Step 7 — Admin user
  admin_name: '', admin_email: '', admin_password: '',
};

const EMPTY_EDIT = {
  name: '', sap_base_url: '', sap_username: '', sap_password: '',
  sap_client: '100', brand_name: '', brand_color: '#1a73e8', brand_theme: 'default',
  plan: 'starter', max_users: 25, licence_expiry: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d} ${months[parseInt(m,10)-1]} ${y}`;
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function TenantsPage() {
  const { adminKey }          = useAdminAuth();
  const [tenants, setTenants] = useState([]);
  const [channels, setChannels] = useState([]);
  const [plans, setPlans]     = useState([]);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('all');
  const [showWizard, setShowWizard] = useState(false);
  const [error, setError]     = useState('');

  const [editTenant, setEditTenant]   = useState(null);
  const [editForm, setEditForm]       = useState(EMPTY_EDIT);
  const [editError, setEditError]     = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  const load = useCallback(() => {
    Promise.all([listTenants(adminKey), listChannels(adminKey), listPlans(adminKey)])
      .then(([t, c, p]) => { setTenants(t); setChannels(c); setPlans(p); })
      .catch(err => setError(err.message));
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const visible = tenants.filter(t => {
    const matchSearch = t.name.toLowerCase().includes(search.toLowerCase())
      || t.tenant_id.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all'
      || (filter === 'active'   && t.is_active)
      || (filter === 'inactive' && !t.is_active)
      || (filter === 'direct'   && t.channel_id === 'direct')
      || (filter === 'reseller' && t.channel_id !== 'direct');
    return matchSearch && matchFilter;
  });

  function openEdit(t) {
    setEditTenant(t);
    setEditForm({
      name:           t.name          || '',
      sap_base_url:   t.sap_base_url  || '',
      sap_username:   '',
      sap_password:   '',
      sap_client:     '100',
      brand_name:     t.brand_name    || '',
      brand_color:    t.brand_color   || '#1a73e8',
      brand_theme:    t.brand_theme   || 'default',
      plan:           t.plan          || 'starter',
      max_users:      t.max_users     || 25,
      licence_expiry: t.licence_expiry || '',
    });
    setEditError('');
  }

  async function handleEdit(e) {
    e.preventDefault();
    setEditError('');
    setEditSubmitting(true);
    try {
      const body = { ...editForm, max_users: Number(editForm.max_users) };
      if (!body.sap_password) delete body.sap_password;
      await updateTenant(adminKey, editTenant.tenant_id, body);
      setEditTenant(null);
      load();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSubmitting(false);
    }
  }

  const [toggleTarget, setToggleTarget]   = useState(null);
  const [toggling, setToggling]           = useState(false);
  const [toggleError, setToggleError]     = useState('');

  async function confirmToggle() {
    setToggling(true);
    setToggleError('');
    try {
      toggleTarget.is_active
        ? await deactivateTenant(adminKey, toggleTarget.tenant_id)
        : await activateTenant(adminKey, toggleTarget.tenant_id);
      setToggleTarget(null);
      load();
    } catch (err) {
      setToggleError(err.message);
      setToggleTarget(null);
    } finally {
      setToggling(false);
    }
  }

  if (error) return (
    <div className="admin-error-page">
      <p>{error}</p>
      <button className="admin-btn-secondary" onClick={load} style={{ marginTop: 12 }}>Retry</button>
    </div>
  );

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h2 className="admin-page-title">Tenants</h2>
        <div className="admin-page-actions">
          <button className="admin-btn-icon" onClick={load} title="Refresh"><RefreshCw size={15} /></button>
          <button className="admin-btn-primary" onClick={() => setShowWizard(true)}>
            <Plus size={15} /> Onboard Tenant
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters">
        <div className="admin-search-wrap">
          <Search size={14} className="admin-search-icon" />
          <input
            className="admin-input admin-search"
            placeholder="Search by name or ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="admin-filter-tabs">
          {['all','active','inactive','direct','reseller'].map(f => (
            <button
              key={f}
              className={`admin-filter-tab${filter === f ? ' active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Tenant ID</th><th>Name</th><th>Channel</th>
              <th>Users</th><th>Licence Expiry</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={7} className="admin-table-empty">No tenants found</td></tr>
            )}
            {visible.map(t => (
              <tr key={t.tenant_id}>
                <td><code>{t.tenant_id}</code></td>
                <td>{t.name}</td>
                <td><span className={`admin-tag ${t.channel_id === 'direct' ? 'direct' : 'reseller'}`}>{t.channel_id || 'direct'}</span></td>
                <td>{t.user_count} / {t.max_users}</td>
                <td>{t.licence_expiry || <span className="admin-muted">—</span>}</td>
                <td><span className={`admin-badge ${t.is_active ? 'active' : 'inactive'}`}>{t.is_active ? 'Active' : 'Inactive'}</span></td>
                <td style={{ display: 'flex', gap: 6 }}>
                  <button className="admin-btn-sm" style={{ background: 'var(--clr-bg-elevated)', color: 'var(--clr-text-secondary)' }} onClick={() => openEdit(t)}>
                    <Pencil size={11} /> Edit
                  </button>
                  <button
                    className={`admin-btn-sm ${t.is_active ? 'danger' : 'success'}`}
                    onClick={() => setToggleTarget(t)}
                  >
                    {t.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tenant activate/deactivate confirmation */}
      {toggleTarget && (
        <div className="admin-modal-backdrop" onClick={() => setToggleTarget(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 className="admin-modal-title">{toggleTarget.is_active ? 'Deactivate' : 'Activate'} Tenant</h3>
            <p style={{ fontSize: 13, color: 'var(--clr-text-secondary)', lineHeight: 1.6, margin: '12px 0 20px' }}>
              {toggleTarget.is_active
                ? <><strong>{toggleTarget.name}</strong> and all its users will lose access. This can be reversed at any time.</>
                : <><strong>{toggleTarget.name}</strong> will regain access for all its users.</>
              }
            </p>
            {toggleError && <p className="admin-error" style={{ marginBottom: 12 }}>{toggleError}</p>}
            <div className="admin-form-actions">
              <button className="admin-btn-secondary" onClick={() => setToggleTarget(null)}>Cancel</button>
              <button className={`admin-btn-primary${toggleTarget.is_active ? ' danger' : ''}`} onClick={confirmToggle} disabled={toggling}>
                {toggling ? 'Saving…' : (toggleTarget.is_active ? 'Deactivate' : 'Activate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTenant && (
        <div className="admin-modal-backdrop" onClick={() => setEditTenant(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3 className="admin-modal-title">Edit — {editTenant.name}</h3>
            <p style={{ fontSize: '0.78rem', color: 'var(--clr-text-muted)', marginTop: -12, marginBottom: 16 }}>
              Tenant ID <code>{editTenant.tenant_id}</code> · Channel <code>{editTenant.channel_id}</code> (immutable)
            </p>
            <form onSubmit={handleEdit} className="admin-form">
              <div className="admin-form-section">Tenant Details</div>
              <div className="admin-form-row">
                <label>Company Name<input className="admin-input" value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} /></label>
                <label>
                  Plan
                  <select className="admin-input" value={editForm.plan} onChange={e => {
                    const p = e.target.value;
                    setEditForm(f => ({ ...f, plan: p, max_users: Math.min(Number(f.max_users), PLAN_LIMITS[p]) }));
                  }}>
                    {Object.keys(PLAN_LIMITS).map(p => (
                      <option key={p} value={p} style={{ textTransform: 'capitalize' }}>{p.charAt(0).toUpperCase() + p.slice(1)} (max {PLAN_LIMITS[p] === 999 ? '∞' : PLAN_LIMITS[p]})</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="admin-form-row">
                <label>
                  Max Users
                  <input type="number" className="admin-input" value={editForm.max_users} min={1} max={PLAN_LIMITS[editForm.plan]}
                    onChange={e => setEditForm(f => ({...f, max_users: Math.min(Number(e.target.value), PLAN_LIMITS[f.plan])}))} />
                  <span style={{ fontSize: '0.75rem', color: 'var(--clr-text-muted)', marginTop: 2 }}>
                    Cap: {PLAN_LIMITS[editForm.plan] === 999 ? 'unlimited' : PLAN_LIMITS[editForm.plan]}
                  </span>
                </label>
                <label>Licence Expiry<input type="date" className="admin-input" value={editForm.licence_expiry} min={new Date().toISOString().split('T')[0]} onChange={e => setEditForm(f => ({...f, licence_expiry: e.target.value}))} /></label>
              </div>
              <div className="admin-form-section">Branding</div>
              <div className="admin-form-row">
                <label>Brand Name<input className="admin-input" value={editForm.brand_name} onChange={e => setEditForm(f => ({...f, brand_name: e.target.value}))} /></label>
                <label>
                  Accent Colour
                  <input type="color" className="admin-input admin-color" value={editForm.brand_color} onChange={e => setEditForm(f => ({...f, brand_color: e.target.value}))} />
                </label>
              </div>
              <div style={{ marginBottom: 4, fontSize: '0.78rem', color: 'var(--clr-text-muted)' }}>App Theme</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 8 }}>
                {Object.entries(THEMES).map(([key, t]) => (
                  <ThemeSwatch
                    key={key} themeKey={key} label={t.label} preview={t.preview}
                    selected={editForm.brand_theme === key}
                    onClick={() => setEditForm(f => ({...f, brand_theme: key}))}
                  />
                ))}
              </div>
              <div className="admin-form-section">SAP Connection</div>
              <div className="admin-form-row">
                <label>SAP Base URL<input className="admin-input" value={editForm.sap_base_url} onChange={e => setEditForm(f => ({...f, sap_base_url: e.target.value}))} placeholder="http://sap.client.com:8000" /></label>
                <label>SAP Client<input className="admin-input" value={editForm.sap_client} onChange={e => setEditForm(f => ({...f, sap_client: e.target.value}))} /></label>
              </div>
              <div className="admin-form-row">
                <label>SAP Username<input className="admin-input" value={editForm.sap_username} onChange={e => setEditForm(f => ({...f, sap_username: e.target.value}))} /></label>
                <label>
                  SAP Password
                  <input type="password" className="admin-input" value={editForm.sap_password} onChange={e => setEditForm(f => ({...f, sap_password: e.target.value}))} placeholder="Leave blank to keep existing" />
                </label>
              </div>
              {editError && <p className="admin-error">{editError}</p>}
              <div className="admin-form-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setEditTenant(null)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={editSubmitting}>{editSubmitting ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Onboarding Wizard */}
      {showWizard && (
        <OnboardingWizard
          adminKey={adminKey}
          channels={channels}
          onClose={() => setShowWizard(false)}
          onSuccess={() => { setShowWizard(false); load(); }}
        />
      )}
    </div>
  );
}


// ── Onboarding Wizard ─────────────────────────────────────────────────────────

function OnboardingWizard({ adminKey, channels, onClose, onSuccess }) {
  const activeChannels = channels.filter(c => c.is_active);
  const [step, setStep]   = useState(0);  // 0-indexed
  const [form, setForm]   = useState({ ...EMPTY_WIZARD, channel_id: activeChannels[0]?.channel_id || '' });
  const [errors, setErrors] = useState({});
  const [testing, setTesting]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone]   = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const total = WIZARD_STEPS.length;

  // ── Validation per step ────────────────────────────────────────────────────

  function validate(s) {
    const errs = {};
    if (s === 0) {
      if (!form.tenant_id.trim())   errs.tenant_id    = 'Required';
      else if (!/^[a-z0-9_-]+$/.test(form.tenant_id)) errs.tenant_id = 'Lowercase letters, digits, hyphens only';
      if (!form.name.trim())        errs.name         = 'Required';
      if (!form.contact_name.trim())errs.contact_name = 'Required';
      if (!form.contact_email.trim() || !form.contact_email.includes('@')) errs.contact_email = 'Valid email required';
      if (activeChannels.length === 0) errs.channel_id = 'No active channels — create a channel first';
      else if (!form.channel_id || !activeChannels.find(c => c.channel_id === form.channel_id))
        errs.channel_id = 'Select a channel';
    }
    if (s === 1) {
      if (!form.sap_base_url.trim()) errs.sap_base_url = 'Required';
      if (!form.sap_username.trim()) errs.sap_username  = 'Required';
      if (!form.sap_password.trim()) errs.sap_password  = 'Required';
    }
    if (s === 3) {
      if (!form.modules || form.modules.length === 0)
        errs.modules = 'Select at least one SAP module';
    }
    if (s === 4) {
      const todayStr = new Date().toISOString().split('T')[0];
      if (!form.licence_expiry) {
        errs.licence_expiry = 'Licence expiry is required';
      } else if (form.licence_expiry < todayStr) {
        errs.licence_expiry = 'Expiry date must be today or in the future';
      }
    }
    if (s === 6) {
      if (!form.admin_name.trim())    errs.admin_name    = 'Required';
      if (!form.admin_email.trim() || !form.admin_email.includes('@')) errs.admin_email = 'Valid email required';
      if (form.admin_password.length < 6) errs.admin_password = 'Minimum 6 characters';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // ── SAP Connection Test (Step 3) ───────────────────────────────────────────

  async function runSapTest() {
    setTesting(true);
    set('sap_test_result', null);
    try {
      const result = await testSapConnection(adminKey, {
        sap_base_url: form.sap_base_url,
        sap_username: form.sap_username,
        sap_password: form.sap_password,
        sap_client:   form.sap_client,
      });
      set('sap_test_result', result);
    } catch (err) {
      set('sap_test_result', { success: false, message: err.message, response_time_ms: 0 });
    } finally {
      setTesting(false);
    }
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  function handleNext() {
    if (!validate(step)) return;
    if (step === 2 && !form.sap_test_result) {
      // Force test before proceeding
      runSapTest();
      return;
    }
    setStep(s => Math.min(s + 1, total - 1));
  }

  function handleBack() {
    setErrors({});
    // Going back from the Test SAP step resets the test result so the user
    // doesn't see a stale result if they change credentials and return.
    if (step === 2) set('sap_test_result', null);
    setStep(s => Math.max(s - 1, 0));
  }

  // ── Final Submit ───────────────────────────────────────────────────────────

  async function handleSubmit() {
    setSubmitError('');
    setSubmitting(true);
    try {
      const body = {
        tenant_id:      form.tenant_id,
        name:           form.name,
        channel_id:     form.channel_id || 'direct',
        sap_base_url:   form.sap_base_url   || undefined,
        sap_username:   form.sap_username   || undefined,
        sap_password:   form.sap_password   || undefined,
        sap_client:     form.sap_client     || '100',
        brand_name:     form.brand_name     || undefined,
        brand_color:    form.brand_color,
        brand_theme:    form.brand_theme,
        plan:           form.plan,
        max_users:      Number(form.max_users),
        licence_expiry: form.licence_expiry || undefined,
        modules:        form.modules,
        admin_name:     form.admin_name,
        admin_email:    form.admin_email,
        admin_password: form.admin_password,
      };
      await onboardTenant(adminKey, body);
      setDone(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Success screen ─────────────────────────────────────────────────────────

  if (done) {
    return (
      <div className="admin-modal-backdrop">
        <div className="admin-modal" style={{ textAlign: 'center', padding: '48px 40px' }}>
          <CheckCircle size={56} style={{ color: 'var(--clr-success)', margin: '0 auto 16px' }} />
          <h3 className="admin-modal-title" style={{ margin: '0 0 8px', textAlign: 'center' }}>Tenant Onboarded!</h3>
          <p style={{ color: 'var(--clr-text-secondary)', marginBottom: 8 }}>
            <strong>{form.name}</strong> is now live.
          </p>
          <p style={{ color: 'var(--clr-text-secondary)', fontSize: '0.875rem', marginBottom: 24 }}>
            Admin <strong>{form.admin_email}</strong> can log in immediately.
          </p>
          <div style={{ background: 'var(--clr-bg-elevated)', borderRadius: 8, padding: '10px 16px', marginBottom: 24, display: 'inline-block' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', color: 'var(--clr-accent-light)' }}>
              Tenant ID: {form.tenant_id}
            </span>
          </div>
          <br />
          <button className="admin-btn-primary" onClick={onSuccess}>Done</button>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="admin-modal-backdrop">
      <div className="admin-modal" style={{ maxWidth: 700, position: 'relative' }}>

        {/* Close button */}
        <button
          onClick={() => {
            if (step > 0 && !done) {
              if (!window.confirm('Close the wizard? All entered information will be lost.')) return;
            }
            onClose();
          }}
          style={{
            position: 'absolute', top: 14, right: 16, background: 'none',
            border: 'none', cursor: 'pointer', color: 'var(--clr-text-muted)',
            padding: 4, lineHeight: 1, fontSize: 18,
          }}
          title="Close wizard"
        >✕</button>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
          {WIZARD_STEPS.map((s, i) => {
            const Icon = s.icon;
            const active  = i === step;
            const done_   = i < step;
            return (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', margin: '0 auto 4px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: done_ ? 'var(--clr-success-bg)' : active ? 'var(--clr-accent-muted)' : 'var(--clr-bg-elevated)',
                  border: active ? '2px solid var(--clr-accent)' : '2px solid transparent',
                  color: done_ ? 'var(--clr-success)' : active ? 'var(--clr-accent-light)' : 'var(--clr-text-muted)',
                  fontSize: 13,
                }}>
                  {done_ ? <CheckCircle size={14} /> : <Icon size={14} />}
                </div>
                <div style={{ fontSize: '0.65rem', color: active ? 'var(--clr-accent-light)' : 'var(--clr-text-muted)', whiteSpace: 'nowrap' }}>{s.label}</div>
              </div>
            );
          })}
        </div>

        <h3 className="admin-modal-title" style={{ marginBottom: 20 }}>
          Step {step + 1} of {total} — {WIZARD_STEPS[step].label}
        </h3>

        {/* ── Step content ── */}
        <div style={{ minHeight: 260 }}>
          {step === 0 && <StepCompany form={form} set={set} errors={errors} channels={channels} />}
          {step === 1 && <StepSap form={form} set={set} errors={errors} />}
          {step === 2 && <StepTestSap form={form} testing={testing} onTest={runSapTest} />}
          {step === 3 && <StepModules form={form} set={set} errors={errors} />}
          {step === 4 && <StepPlan form={form} set={set} errors={errors} />}
          {step === 5 && <StepBranding form={form} set={set} />}
          {step === 6 && <StepAdmin form={form} set={set} errors={errors} />}
          {step === 7 && <StepSummary form={form} />}
        </div>

        {submitError && <p className="admin-error" style={{ marginTop: 12 }}>{submitError}</p>}

        {/* Hint shown on Test SAP step until test is run */}
        {step === 2 && !form.sap_test_result && !testing && (
          <p style={{ fontSize: '0.78rem', color: 'var(--clr-text-muted)', textAlign: 'right', marginTop: 8 }}>
            Run the connection test above to enable Next, or go Back to skip SAP setup.
          </p>
        )}

        {/* Navigation */}
        <div className="admin-form-actions" style={{ marginTop: 8 }}>
          <button type="button" className="admin-btn-secondary" onClick={handleBack} disabled={step === 0}>
            <ChevronLeft size={14} /> Back
          </button>
          <div style={{ flex: 1 }} />
          {step < total - 1 ? (
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleNext}
              disabled={testing || (step === 2 && !form.sap_test_result)}
            >
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button type="button" className="admin-btn-primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Onboarding…' : 'Confirm & Onboard'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Step 1: Company Details ───────────────────────────────────────────────────

function StepCompany({ form, set, errors, channels }) {
  const activeChannels = channels.filter(c => c.is_active);
  return (
    <div className="admin-form" style={{ gap: 16 }}>
      {activeChannels.length === 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 14px', fontSize: '0.85rem', color: '#92400e' }}>
          ⚠️ No active channels found. You must{' '}
          <a href="/inqlect-admin/channels" style={{ color: '#b45309', fontWeight: 600 }}>create a channel</a>
          {' '}before onboarding a tenant.
        </div>
      )}
      <div className="admin-form-row">
        <label>
          Tenant ID (slug) *
          <input className="admin-input" value={form.tenant_id} placeholder="e.g. wipro_client1"
            onChange={e => set('tenant_id', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} />
          {errors.tenant_id && <span className="admin-error">{errors.tenant_id}</span>}
        </label>
        <label>
          Company Name *
          <input className="admin-input" value={form.name} placeholder="e.g. Wipro Client Ltd"
            onChange={e => set('name', e.target.value)} />
          {errors.name && <span className="admin-error">{errors.name}</span>}
        </label>
      </div>
      <div className="admin-form-row">
        <label>
          Industry
          <select className="admin-input" value={form.industry} onChange={e => set('industry', e.target.value)}>
            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </label>
        <label>
          Channel *
          <select className="admin-input" value={form.channel_id} onChange={e => set('channel_id', e.target.value)}
            disabled={activeChannels.length === 0}>
            {activeChannels.length === 0
              ? <option value="">— No channels available —</option>
              : activeChannels.map(c => (
                  <option key={c.channel_id} value={c.channel_id}>{c.name} ({c.type})</option>
                ))
            }
          </select>
          {errors.channel_id && <span className="admin-error">{errors.channel_id}</span>}
        </label>
      </div>
      <div className="admin-form-row">
        <label>
          Contact Person *
          <input className="admin-input" value={form.contact_name} placeholder="Primary contact name"
            onChange={e => set('contact_name', e.target.value)} />
          {errors.contact_name && <span className="admin-error">{errors.contact_name}</span>}
        </label>
        <label>
          Contact Email *
          <input type="email" className="admin-input" value={form.contact_email} placeholder="contact@company.com"
            onChange={e => set('contact_email', e.target.value)} />
          {errors.contact_email && <span className="admin-error">{errors.contact_email}</span>}
        </label>
      </div>
    </div>
  );
}


// ── Step 2: SAP System ────────────────────────────────────────────────────────

function StepSap({ form, set, errors }) {
  return (
    <div className="admin-form" style={{ gap: 16 }}>
      <div className="admin-form-row">
        <label>
          SAP Base URL *
          <input className="admin-input" value={form.sap_base_url} placeholder="http://sap.client.com:8000"
            onChange={e => set('sap_base_url', e.target.value)} />
          {errors.sap_base_url && <span className="admin-error">{errors.sap_base_url}</span>}
        </label>
        <label>
          SAP Client
          <input className="admin-input" value={form.sap_client} placeholder="100"
            onChange={e => set('sap_client', e.target.value)} />
        </label>
      </div>
      <div className="admin-form-row">
        <label>
          SAP Username *
          <input className="admin-input" value={form.sap_username} placeholder="ABAP_USER"
            onChange={e => set('sap_username', e.target.value)} />
          {errors.sap_username && <span className="admin-error">{errors.sap_username}</span>}
        </label>
        <label>
          SAP Password *
          <input type="password" className="admin-input" value={form.sap_password}
            onChange={e => set('sap_password', e.target.value)} />
          {errors.sap_password && <span className="admin-error">{errors.sap_password}</span>}
        </label>
      </div>
      <label style={{ fontSize: '0.8rem', color: 'var(--clr-text-secondary)', display: 'flex', flexDirection: 'column', gap: 6 }}>
        SAP Version
        <div style={{ display: 'flex', gap: 12 }}>
          {SAP_VERSIONS.map(v => (
            <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem', color: 'var(--clr-text-primary)' }}>
              <input type="radio" name="sap_version" value={v} checked={form.sap_version === v}
                onChange={() => set('sap_version', v)} />
              {v}
            </label>
          ))}
        </div>
      </label>
    </div>
  );
}


// ── Step 3: Test SAP Connection ───────────────────────────────────────────────

function StepTestSap({ form, testing, onTest }) {
  const result = form.sap_test_result;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 20 }}>
      <p style={{ color: 'var(--clr-text-secondary)', textAlign: 'center', maxWidth: 420 }}>
        Click below to verify the SAP credentials entered in the previous step.<br />
        A lightweight OData call will be made to the SAP system.
      </p>

      <div style={{ background: 'var(--clr-bg-elevated)', borderRadius: 10, padding: '12px 20px', fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--clr-text-secondary)' }}>
        {form.sap_base_url || 'No URL entered'} &nbsp;·&nbsp; Client {form.sap_client}
      </div>

      <button className="admin-btn-primary" onClick={onTest} disabled={testing}
        style={{ minWidth: 180 }}>
        {testing ? <><Loader size={14} className="spin" /> Testing…</> : <><Zap size={14} /> Test SAP Connection</>}
      </button>

      {result && (() => {
        const isOk      = result.success;
        const isWarn    = !result.success && result.reachable;
        const isFail    = !result.success && !result.reachable;
        const bg    = isOk ? 'var(--clr-success-bg)' : isWarn ? 'rgba(234,179,8,0.12)' : 'var(--clr-error-bg)';
        const clr   = isOk ? 'var(--clr-success)'    : isWarn ? '#ca8a04'                : 'var(--clr-error)';
        const label = isOk ? 'Connected'              : isWarn ? 'Server reachable — check credentials' : 'Cannot reach SAP server';
        const Icon  = isOk ? CheckCircle              : isWarn ? AlertTriangle            : XCircle;
        return (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '12px 20px', borderRadius: 10, background: bg, color: clr,
            width: '100%', maxWidth: 420,
          }}>
            <Icon size={18} style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600 }}>{label}</div>
              <div style={{ fontSize: '0.82rem', opacity: 0.85 }}>{result.message}</div>
              {result.response_time_ms > 0 && (
                <div style={{ fontSize: '0.78rem', opacity: 0.7, marginTop: 2 }}>Response: {result.response_time_ms} ms</div>
              )}
            </div>
          </div>
        );
      })()}

      {result && !result.success && (
        <p style={{ color: 'var(--clr-text-muted)', fontSize: '0.82rem', textAlign: 'center' }}>
          You can still proceed — SAP connection can be configured later.
        </p>
      )}
    </div>
  );
}


// ── Step 4: Module Selection ──────────────────────────────────────────────────

function StepModules({ form, set, errors }) {
  function toggle(m) {
    set('modules', form.modules.includes(m)
      ? form.modules.filter(x => x !== m)
      : [...form.modules, m]);
  }
  return (
    <div>
      <p style={{ color: 'var(--clr-text-secondary)', marginBottom: 20, fontSize: '0.875rem' }}>
        Select the SAP modules this tenant will have access to. This controls which sample queries and module chips are shown.
      </p>
      {errors?.modules && <p className="admin-error" style={{ marginBottom: 12 }}>{errors.modules}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {MODULES.map(m => {
          const checked = form.modules.includes(m);
          return (
            <label key={m} style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              padding: '10px 14px', borderRadius: 8, border: `1px solid ${checked ? 'var(--clr-accent)' : 'var(--clr-border)'}`,
              background: checked ? 'var(--clr-accent-muted)' : 'var(--clr-bg-elevated)',
              transition: 'all 0.15s',
            }}>
              <input type="checkbox" checked={checked} onChange={() => toggle(m)} style={{ accentColor: 'var(--clr-accent)' }} />
              <span style={{ fontWeight: 600, color: checked ? 'var(--clr-accent-light)' : 'var(--clr-text-primary)' }}>{m}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}


// ── Step 5: Plan + Licence ────────────────────────────────────────────────────

function StepPlan({ form, set, errors }) {
  const today = new Date().toISOString().split('T')[0];
  function selectPlan(p) {
    set('plan', p);
    set('max_users', PLAN_LIMITS[p]);
  }
  return (
    <div className="admin-form" style={{ gap: 20 }}>
      <div>
        <p style={{ color: 'var(--clr-text-secondary)', marginBottom: 14, fontSize: '0.875rem' }}>Select a plan — max users are capped to the plan limit.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          {Object.entries(PLAN_LIMITS).map(([plan, maxU]) => {
            const active = form.plan === plan;
            return (
              <button key={plan} type="button" onClick={() => selectPlan(plan)} style={{
                padding: '14px 12px', borderRadius: 10, border: `2px solid ${active ? 'var(--clr-accent)' : 'var(--clr-border)'}`,
                background: active ? 'var(--clr-accent-muted)' : 'var(--clr-bg-elevated)',
                cursor: 'pointer', textAlign: 'center',
              }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: active ? 'var(--clr-accent-light)' : 'var(--clr-text-primary)', textTransform: 'capitalize' }}>{plan}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--clr-text-muted)', marginTop: 4 }}>{maxU === 999 ? 'Unlimited' : `Up to ${maxU} users`}</div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="admin-form-row">
        <label>
          Max Users
          <input type="number" className="admin-input" value={form.max_users} min={1} max={PLAN_LIMITS[form.plan]}
            onChange={e => set('max_users', Math.min(Number(e.target.value), PLAN_LIMITS[form.plan]))} />
          <span style={{ fontSize: '0.75rem', color: 'var(--clr-text-muted)', marginTop: 2 }}>
            Max for {form.plan}: {PLAN_LIMITS[form.plan] === 999 ? 'unlimited' : PLAN_LIMITS[form.plan]}
          </span>
        </label>
        <label>
          Licence Expiry *
          <input type="date" className="admin-input" value={form.licence_expiry} min={today} required
            onChange={e => set('licence_expiry', e.target.value)} />
          {errors?.licence_expiry && <span className="admin-error">{errors.licence_expiry}</span>}
        </label>
      </div>
    </div>
  );
}


// ── Step 6: Branding ──────────────────────────────────────────────────────────

// Curated 16-color palette — 4 columns × 4 rows
const BRAND_PALETTE = [
  // Blues
  { hex: '#2563EB', label: 'Sapphire' },
  { hex: '#3B82F6', label: 'Blue' },
  { hex: '#60A5FA', label: 'Sky' },
  { hex: '#0EA5E9', label: 'Cyan' },
  // Purples
  { hex: '#7C3AED', label: 'Violet' },
  { hex: '#8B5CF6', label: 'Purple' },
  { hex: '#A855F7', label: 'Fuchsia' },
  { hex: '#EC4899', label: 'Pink' },
  // Greens / Teals
  { hex: '#059669', label: 'Emerald' },
  { hex: '#10B981', label: 'Teal' },
  { hex: '#14B8A6', label: 'Seafoam' },
  { hex: '#22C55E', label: 'Green' },
  // Warm / Neutral
  { hex: '#F59E0B', label: 'Amber' },
  { hex: '#EF4444', label: 'Rose' },
  { hex: '#F97316', label: 'Orange' },
  { hex: '#64748B', label: 'Slate' },
];

function hexLuminance(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = v => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function isValidHex(val) {
  return /^#[0-9A-Fa-f]{6}$/.test(val);
}

function ThemeSwatch({ themeKey, label, preview, selected, onClick }) {
  const [bg, accent, text] = preview;
  return (
    <button type="button" onClick={onClick} style={{
      padding: '10px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
      border: `2px solid ${selected ? 'var(--clr-accent)' : 'var(--clr-border)'}`,
      background: selected ? 'var(--clr-accent-muted)' : 'var(--clr-bg-elevated)',
      display: 'flex', flexDirection: 'column', gap: 8,
      transition: 'border-color 150ms ease, background 150ms ease',
    }}>
      {/* Mini app preview */}
      <div style={{ borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', height: 48, display: 'flex' }}>
        <div style={{ width: 18, background: bg, borderRight: `2px solid ${accent}33` }} />
        <div style={{ flex: 1, background: bg, padding: '5px 7px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ height: 4, width: '65%', borderRadius: 2, background: accent }} />
          <div style={{ height: 3, width: '85%', borderRadius: 2, background: text, opacity: 0.35 }} />
          <div style={{ height: 3, width: '50%', borderRadius: 2, background: text, opacity: 0.2 }} />
          <div style={{ height: 4, width: '70%', borderRadius: 2, background: accent, opacity: 0.45, marginTop: 3 }} />
        </div>
      </div>
      <span style={{ fontSize: '0.72rem', color: selected ? 'var(--clr-accent-light)' : 'var(--clr-text-secondary)', fontWeight: selected ? 600 : 400 }}>
        {label}
      </span>
    </button>
  );
}

function BrandColorPicker({ value, onChange }) {
  const [hexInput, setHexInput] = React.useState(value);
  const [inputError, setInputError] = React.useState(false);

  // Keep hex input in sync when palette swatch is clicked
  React.useEffect(() => { setHexInput(value); setInputError(false); }, [value]);

  const lum = isValidHex(value) ? hexLuminance(value) : null;
  const contrastOnDark = lum !== null ? (lum + 0.05) / (0.0 + 0.05) : null;
  const contrastOnLight = lum !== null ? (1.05) / (lum + 0.05) : null;
  const darkOk  = contrastOnDark  !== null && contrastOnDark  >= 3;
  const lightOk = contrastOnLight !== null && contrastOnLight >= 3;

  function handleHexInput(e) {
    const raw = e.target.value;
    setHexInput(raw);
    const normalised = raw.startsWith('#') ? raw : '#' + raw;
    if (isValidHex(normalised)) {
      setInputError(false);
      onChange(normalised);
    } else {
      setInputError(true);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Palette grid — 4 × 4 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {BRAND_PALETTE.map(({ hex, label }) => {
          const isSelected = value.toLowerCase() === hex.toLowerCase();
          return (
            <button
              key={hex}
              type="button"
              title={label}
              onClick={() => onChange(hex)}
              style={{
                width: '100%',
                aspectRatio: '1',
                borderRadius: 8,
                background: hex,
                border: isSelected ? '3px solid #fff' : '2px solid rgba(255,255,255,0.12)',
                cursor: 'pointer',
                boxShadow: isSelected ? `0 0 0 2px ${hex}, 0 2px 8px ${hex}66` : '0 1px 4px rgba(0,0,0,0.3)',
                transition: 'transform 120ms ease, box-shadow 120ms ease',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {isSelected && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7L5.5 10.5L12 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom hex input row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
        {/* Live preview swatch */}
        <div style={{
          width: 36, height: 36, borderRadius: 6, flexShrink: 0,
          background: isValidHex(value) ? value : '#888',
          border: '1px solid rgba(255,255,255,0.15)',
          boxShadow: isValidHex(value) ? `0 2px 8px ${value}55` : 'none',
        }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <input
            value={hexInput}
            onChange={handleHexInput}
            placeholder="#3B82F6"
            maxLength={7}
            spellCheck={false}
            style={{
              background: 'var(--clr-bg-elevated)',
              border: `1px solid ${inputError ? 'var(--clr-error)' : 'var(--clr-border)'}`,
              borderRadius: 6, padding: '6px 10px',
              color: 'var(--clr-text-primary)', fontFamily: 'monospace',
              fontSize: '0.85rem', outline: 'none', width: '100%',
            }}
          />
          {inputError && (
            <span style={{ fontSize: '0.7rem', color: 'var(--clr-error)' }}>Enter a valid hex like #3B82F6</span>
          )}
        </div>
      </div>

      {/* Contrast safety badge */}
      {lum !== null && (
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: '0.72rem', fontWeight: 600,
            padding: '4px 10px', borderRadius: 20,
            background: darkOk ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
            color: darkOk ? '#10B981' : '#EF4444',
            border: `1px solid ${darkOk ? '#10B98140' : '#EF444440'}`,
          }}>
            {darkOk ? '✓' : '⚠'} Dark theme
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: '0.72rem', fontWeight: 600,
            padding: '4px 10px', borderRadius: 20,
            background: lightOk ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
            color: lightOk ? '#10B981' : '#EF4444',
            border: `1px solid ${lightOk ? '#10B98140' : '#EF444440'}`,
          }}>
            {lightOk ? '✓' : '⚠'} Light theme
          </span>
          {(!darkOk || !lightOk) && (
            <span style={{ fontSize: '0.7rem', color: 'var(--clr-text-muted)', alignSelf: 'center' }}>
              Low contrast — buttons may be hard to read
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function StepBranding({ form, set }) {
  const themePreview = THEMES[form.brand_theme] || THEMES.default;
  const previewBg = themePreview.preview[0];
  return (
    <div className="admin-form" style={{ gap: 24 }}>
      {/* Theme preset grid */}
      <div>
        <p style={{ fontSize: '0.8rem', color: 'var(--clr-text-secondary)', marginBottom: 12 }}>
          Choose a theme — sets the full background, text and border palette for this tenant's app.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {Object.entries(THEMES).map(([key, t]) => (
            <ThemeSwatch
              key={key} themeKey={key} label={t.label} preview={t.preview}
              selected={form.brand_theme === key}
              onClick={() => set('brand_theme', key)}
            />
          ))}
        </div>
      </div>

      {/* Brand name */}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--clr-text-secondary)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Brand Name</span>
        <input className="admin-input" value={form.brand_name} placeholder="e.g. XYZ SAP Assistant"
          onChange={e => set('brand_name', e.target.value)} />
      </label>

      {/* Accent colour picker */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--clr-text-secondary)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Accent Colour
        </span>
        <p style={{ fontSize: '0.78rem', color: 'var(--clr-text-muted)', margin: 0 }}>
          Used for buttons, active links and highlights across the app.
        </p>
        <BrandColorPicker value={form.brand_color} onChange={v => set('brand_color', v)} />
      </div>

      {/* Live preview */}
      <div>
        <p style={{ fontSize: '0.78rem', color: 'var(--clr-text-muted)', marginBottom: 10 }}>Preview:</p>
        <div style={{
          borderRadius: 10, overflow: 'hidden', border: '1px solid var(--clr-border)',
          display: 'flex', height: 110,
        }}>
          {/* Sidebar */}
          <div style={{ width: 140, background: previewBg, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: form.brand_color }}>{form.brand_name || 'Querely'}</div>
            <div style={{ fontSize: '0.62rem', color: themePreview.preview[1], opacity: 0.7 }}>{form.brand_name || 'Brand Name'}</div>
            <div style={{ marginTop: 'auto', fontSize: '0.62rem', padding: '3px 6px', borderRadius: 4, background: form.brand_color + '20', borderLeft: `2px solid ${form.brand_color}`, color: themePreview.preview[2], opacity: 0.8 }}>
              New chat
            </div>
          </div>
          {/* Chat area */}
          <div style={{ flex: 1, background: previewBg, opacity: 0.85, padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 6 }}>
            <div style={{ alignSelf: 'flex-end', background: form.brand_color, color: '#fff', fontSize: '0.65rem', padding: '4px 8px', borderRadius: 8, maxWidth: '60%' }}>Show sales orders</div>
            <div style={{ background: previewBg, border: `1px solid ${form.brand_color}30`, color: themePreview.preview[2], fontSize: '0.65rem', padding: '4px 8px', borderRadius: 8, maxWidth: '80%' }}>Found 24 records in SAP SD…</div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Step 7: First Admin User ──────────────────────────────────────────────────

function StepAdmin({ form, set, errors }) {
  return (
    <div className="admin-form" style={{ gap: 16 }}>
      <p style={{ color: 'var(--clr-text-secondary)', fontSize: '0.875rem', margin: 0 }}>
        This user will be created as the first admin for this tenant. They can log in immediately after onboarding.
      </p>
      <div className="admin-form-row">
        <label>
          Admin Name *
          <input className="admin-input" value={form.admin_name} placeholder="Full name"
            onChange={e => set('admin_name', e.target.value)} />
          {errors.admin_name && <span className="admin-error">{errors.admin_name}</span>}
        </label>
        <label>
          Admin Email *
          <input type="email" className="admin-input" value={form.admin_email} placeholder="admin@client.com"
            onChange={e => set('admin_email', e.target.value)} />
          {errors.admin_email && <span className="admin-error">{errors.admin_email}</span>}
        </label>
      </div>
      <label style={{ fontSize: '0.8rem', color: 'var(--clr-text-secondary)', display: 'flex', flexDirection: 'column', gap: 6 }}>
        Temporary Password * (min 6 characters)
        <input type="password" className="admin-input" value={form.admin_password}
          onChange={e => set('admin_password', e.target.value)} />
        {errors.admin_password && <span className="admin-error">{errors.admin_password}</span>}
      </label>
    </div>
  );
}


// ── Step 8: Summary ───────────────────────────────────────────────────────────

function StepSummary({ form }) {
  function Row({ label, value }) {
    return (
      <tr>
        <td style={{ color: 'var(--clr-text-muted)', fontSize: '0.8rem', padding: '6px 0', width: 160, verticalAlign: 'top' }}>{label}</td>
        <td style={{ color: 'var(--clr-text-primary)', fontSize: '0.875rem', padding: '6px 0', fontWeight: 500 }}>{value || <em style={{ color: 'var(--clr-text-muted)', fontWeight: 400 }}>—</em>}</td>
      </tr>
    );
  }
  return (
    <div>
      <p style={{ color: 'var(--clr-text-secondary)', fontSize: '0.875rem', marginBottom: 16 }}>
        Please review all details before confirming. Click "Confirm &amp; Onboard" to create the tenant.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div className="admin-form-section" style={{ marginBottom: 10 }}>Company</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Tenant ID"     value={<code>{form.tenant_id}</code>} />
              <Row label="Company Name"  value={form.name} />
              <Row label="Industry"      value={form.industry} />
              <Row label="Contact"       value={`${form.contact_name} (${form.contact_email})`} />
              <Row label="Channel"       value={form.channel_id} />
            </tbody>
          </table>

          <div className="admin-form-section" style={{ marginTop: 16, marginBottom: 10 }}>Plan &amp; Licence</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Plan"          value={<span style={{ textTransform: 'capitalize' }}>{form.plan}</span>} />
              <Row label="Max Users"     value={form.max_users} />
              <Row label="Expiry"        value={fmtDate(form.licence_expiry)} />
              <Row label="Modules"       value={form.modules.join(', ') || 'None'} />
            </tbody>
          </table>
        </div>

        <div>
          <div className="admin-form-section" style={{ marginBottom: 10 }}>SAP System</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="SAP URL"       value={form.sap_base_url} />
              <Row label="SAP Username"  value={form.sap_username} />
              <Row label="SAP Client"    value={form.sap_client} />
              <Row label="SAP Version"   value={form.sap_version} />
              <Row label="SAP Test"      value={
                form.sap_test_result
                  ? (form.sap_test_result.success ? '✓ Passed' : '✗ Failed')
                  : 'Not tested'
              } />
            </tbody>
          </table>

          <div className="admin-form-section" style={{ marginTop: 16, marginBottom: 10 }}>Branding &amp; Admin</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Brand Name"    value={form.brand_name} />
              <Row label="Brand Colour"  value={
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', background: form.brand_color, display: 'inline-block', border: '1px solid var(--clr-border)' }} />
                  {form.brand_color}
                </span>
              } />
              <Row label="Admin Name"    value={form.admin_name} />
              <Row label="Admin Email"   value={form.admin_email} />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
